import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  MagnifyingGlass,
  Funnel,
  X,
  Plus,
  SidebarSimple,
  FireSimple,
  Columns,
  FileArrowUp,
  TreeStructure,
  Question,
  CheckSquare,
  Trash,
  DotsThreeOutline,
  ArrowsOut,
  ArrowsIn,
  CalendarCheck,
  Tag,
} from "@phosphor-icons/react";
import { ZOOM_LEVELS } from "@/lib/gantt-utils";
import ViewMenu from "./ViewMenu";
import SavedViewsMenu from "./SavedViewsMenu";

function useFullscreen() {
  const [isFs, setIsFs] = useState(
    typeof document !== "undefined" && !!document.fullscreenElement
  );
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      /* no-op: browser denied or unsupported */
    }
  };
  return { isFs, toggle };
}

export default function Toolbar({
  search,
  setSearch,
  workstreamFilter,
  setWorkstreamFilter,
  statusFilter,
  setStatusFilter,
  statuses,
  onOpenStatuses,
  workstreams,
  onClear,
  zoomKey,
  setZoomKey,
  canEdit,
  onAddTask,
  leftCollapsed,
  onToggleLeftPane,
  onOpenHeatmap,
  onOpenColumns,
  onOpenImport,
  onOpenHelp,
  criticalPathOn,
  onToggleCriticalPath,
  selectMode,
  onToggleSelectMode,
  selectedCount,
  onBulkDelete,
  viewColumns,
  hiddenCols,
  onToggleColumn,
  onShowAllColumns,
  onResetColumnWidths,
  onGoToToday,
  savedViews,
  onApplySavedView,
  onSaveCurrentView,
  onDeleteSavedView,
  canEditProject,
}) {
  const hasFilters = !!search || !!workstreamFilter || !!statusFilter;
  const { isFs, toggle: toggleFullscreen } = useFullscreen();

  return (
    <div className="h-14 bg-white border-b border-slate-200 px-3 md:px-6 flex items-center gap-2 shrink-0 z-10 overflow-x-auto scrollbar-thin" data-testid="toolbar">
      {/* Collapse / expand left pane */}
      <button
        type="button"
        onClick={onToggleLeftPane}
        className={`h-9 w-9 rounded-md border flex items-center justify-center transition-colors shrink-0 ${
          leftCollapsed
            ? "bg-slate-950 text-white border-slate-950 hover:bg-slate-800"
            : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
        }`}
        data-testid="toggle-left-pane"
        title={leftCollapsed ? "Show task table" : "Hide task table"}
      >
        <SidebarSimple size={14} weight="bold" />
      </button>

      {/* Search */}
      <div className="relative flex-1 min-w-[160px] max-w-md">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks & subtasks…"
          className="h-9 pl-9 pr-3 rounded-md border-slate-200 bg-[#F4F7FB] focus-visible:ring-0 focus-visible:border-slate-950 focus-visible:bg-white text-sm"
          data-testid="search-input"
        />
      </div>

      {/* Quick filters */}
      <Select value={workstreamFilter || "__all__"} onValueChange={(v) => setWorkstreamFilter(v === "__all__" ? "" : v)}>
        <SelectTrigger
          className="h-9 w-[150px] rounded-md border-slate-200 bg-[#F4F7FB] text-sm focus:ring-0 focus:border-slate-950 shrink-0"
          data-testid="workstream-filter"
        >
          <Funnel size={12} weight="bold" className="text-slate-500" />
          <SelectValue placeholder="Workstream" />
        </SelectTrigger>
        <SelectContent className="rounded-md">
          <SelectItem value="__all__">All workstreams</SelectItem>
          {workstreams.map((w) => (
            <SelectItem key={w} value={w}>{w}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
        <SelectTrigger
          className="h-9 w-[130px] rounded-md border-slate-200 bg-[#F4F7FB] text-sm focus:ring-0 focus:border-slate-950 shrink-0"
          data-testid="status-filter"
        >
          <Funnel size={12} weight="bold" className="text-slate-500" />
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent className="rounded-md">
          <SelectItem value="__all__">All statuses</SelectItem>
          {(statuses || []).map((s) => (
            <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="h-9 rounded-md text-slate-600 hover:text-slate-950 hover:bg-slate-100 font-medium shrink-0"
          data-testid="clear-filters-btn"
        >
          <X size={14} weight="bold" />
          <span className="ml-1.5">Clear</span>
        </Button>
      )}

      {/* Select tasks — promoted out of the More menu so users can find it.
          Sits in the search/filter row, matching the outline pill styling of
          the other quick filters. Active state inverts to solid slate-950. */}
      {canEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleSelectMode}
          className={`h-9 rounded-md font-medium shrink-0 transition-colors ${
            selectMode
              ? "bg-slate-950 text-white border-slate-950 hover:bg-slate-800"
              : "border-slate-300 hover:border-slate-400 text-slate-700"
          }`}
          data-testid="select-tasks-btn"
          title={selectMode ? "Exit select mode" : "Select multiple tasks for bulk actions"}
        >
          <CheckSquare size={14} weight={selectMode ? "fill" : "bold"} />
          <span className="ml-2">{selectMode ? "Exit select" : "Select tasks"}</span>
        </Button>
      )}

      <div className="flex-1" />

      {/* Zoom */}
      <div className="flex items-center bg-slate-100 border border-slate-200 rounded-md p-0.5 shrink-0" data-testid="zoom-control">
        {Object.values(ZOOM_LEVELS).map((z) => (
          <button
            key={z.key}
            onClick={() => setZoomKey(z.key)}
            className={`px-2 h-7 text-[11px] font-mono-ibm uppercase tracking-widest rounded-md transition-colors ${
              zoomKey === z.key
                ? "bg-slate-950 text-white"
                : "text-slate-600 hover:text-slate-950"
            }`}
            data-testid={`zoom-${z.key}`}
          >
            {z.label}
          </button>
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onGoToToday}
        className="h-9 rounded-md border-slate-300 hover:border-slate-950 text-slate-700 hover:text-slate-950 font-medium shrink-0"
        data-testid="today-btn"
        title="Jump to today's date on the timeline"
      >
        <CalendarCheck size={14} weight="bold" />
        <span className="ml-2">Today</span>
      </Button>

      <SavedViewsMenu
        views={savedViews || []}
        onApply={onApplySavedView}
        onSaveCurrent={onSaveCurrentView}
        onDelete={onDeleteSavedView}
        canEditProject={canEditProject}
      />

      {canEdit && (
        <Button
          size="sm"
          onClick={onAddTask}
          className="h-9 rounded-md bg-slate-950 hover:bg-slate-800 text-white font-medium shrink-0"
          data-testid="add-task-btn"
        >
          <Plus size={14} weight="bold" />
          <span className="ml-2">Add Task</span>
        </Button>
      )}

      {/* Selection: only show the label when active to save room */}
      {canEdit && selectMode && selectedCount > 0 ? (
        <Button
          size="sm"
          onClick={onBulkDelete}
          className="h-9 rounded-md bg-red-600 hover:bg-red-700 text-white font-medium shrink-0"
          data-testid="bulk-delete-btn"
        >
          <Trash size={14} weight="bold" />
          <span className="ml-2">Delete {selectedCount}</span>
        </Button>
      ) : null}

      {/* Overflow "More" menu — hosts secondary actions so the toolbar stays calm */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 rounded-md border-slate-300 hover:border-slate-400 text-slate-700 font-medium shrink-0"
            data-testid="toolbar-more-btn"
            title="More actions"
          >
            <DotsThreeOutline size={14} weight="bold" />
            <span className="ml-1.5">More</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-md w-[220px]">
          {/* Select tasks promoted to the main toolbar — no longer in this menu. */}
          <DropdownMenuItem onClick={onToggleCriticalPath} data-testid="more-toggle-critical-path" className="text-xs">
            <TreeStructure
              size={12}
              weight={criticalPathOn ? "fill" : "bold"}
              className={`mr-2 ${criticalPathOn ? "text-red-500" : ""}`}
            />
            {criticalPathOn ? "Hide critical path" : "Show critical path"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenHeatmap} data-testid="more-open-heatmap" className="text-xs">
            <FireSimple size={12} weight="fill" className="mr-2 text-red-500" />
            Risk Heatmap
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {canEdit && (
            <DropdownMenuItem onClick={onOpenImport} data-testid="more-import" className="text-xs">
              <FileArrowUp size={12} weight="bold" className="mr-2" />
              Import .csv / .xlsx
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onOpenColumns} data-testid="more-columns" className="text-xs">
            <Columns size={12} weight="bold" className="mr-2" />
            Manage columns
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenStatuses} data-testid="more-statuses" className="text-xs">
            <Tag size={12} weight="bold" className="mr-2" />
            Manage statuses
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={toggleFullscreen} data-testid="toggle-fullscreen" className="text-xs">
            {isFs ? (
              <ArrowsIn size={12} weight="bold" className="mr-2" />
            ) : (
              <ArrowsOut size={12} weight="bold" className="mr-2" />
            )}
            {isFs ? "Exit fullscreen" : "Enter fullscreen"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenHelp} data-testid="more-help" className="text-xs">
            <Question size={12} weight="bold" className="mr-2" />
            How to use
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ViewMenu
        columns={viewColumns || []}
        hiddenSet={hiddenCols || new Set()}
        onToggle={onToggleColumn}
        onShowAll={onShowAllColumns}
        onResetWidths={onResetColumnWidths}
      />
    </div>
  );
}
